/**
 * Global keyboard shortcuts for the game (single owner of tile-flip keys).
 *
 * - ArrowLeft / ArrowUp / V: flip vowel
 * - ArrowRight / ArrowDown / C: flip consonant
 *
 * Arrow keys also work while typing in the claim input (it is always focused
 * during play). Other inputs (chat, etc.) keep normal arrow behaviour.
 * Escape is handled in PauseOverlay (pause / clear steal selection).
 */

import { useEffect } from 'react';
import { socketActions } from '../lib/socketActions';
import { useGameStore, selectIsMyTurn } from '../stores/gameStore';

/** Attribute that marks the word-claim input */
export const CLAIM_INPUT_ATTR = 'data-claim-input';

export function isClaimInput(el: EventTarget | null): boolean {
  return el instanceof HTMLElement && el.hasAttribute(CLAIM_INPUT_ATTR);
}

function tryFlip(tileType: 'vowel' | 'consonant', e: KeyboardEvent): void {
  // Read fresh state to avoid stale closures
  const state = useGameStore.getState();
  const gs = state.gameState;
  if (!gs || gs.status !== 'playing' || gs.isPaused) return;
  if (!selectIsMyTurn(state) || state.isFlippingTile) return;
  const remaining = tileType === 'vowel' ? gs.vowelsRemaining : gs.consonantsRemaining;
  if (remaining <= 0) return;
  e.preventDefault();
  socketActions.flipTile(tileType);
}

export function useKeyboardShortcuts() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat) return;
      // Leave browser/OS shortcuts alone
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const status = useGameStore.getState().gameState?.status;
      if (status !== 'playing') return;

      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[role="dialog"]')) return;

      const isInInput =
        !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      const inClaimInput = isClaimInput(target);

      // Arrow keys: anywhere except non-claim text fields
      if (!isInInput || inClaimInput) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          tryFlip('vowel', e);
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          tryFlip('consonant', e);
          return;
        }
      }

      if (isInInput) {
        // Escape in a secondary field (e.g. chat) just leaves the field
        if (e.key === 'Escape' && !inClaimInput && target) {
          e.preventDefault();
          target.blur();
        }
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'v':
          tryFlip('vowel', e);
          break;
        case 'c':
          tryFlip('consonant', e);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
